import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Coins, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail, newIdempotencyKey } from "../lib/api";
import { BigButton } from "../design/BigButton";
import "./rewards.css";

const CATEGORY_TABS = [
  { key: "sticker", label: "Stickers" },
  { key: "avatar_accessory", label: "Accessories" },
  { key: "room_decoration", label: "Room Decor" },
  { key: "bonus_level_pass", label: "Bonus" },
];

export function RewardsRoomScreen() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("sticker");
  const [pending, setPending] = useState(null); // reward to confirm buying
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [flash, setFlash] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/rewards", { params: { childProfileId: profileId } });
      setItems(data.items || []);
      setBalance(data.tokenBalance || 0);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [profileId]);

  const filtered = items.filter((r) => r.category === category);

  async function confirmPurchase() {
    if (!pending) return;
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post("/rewards/purchase", {
        childProfileId: profileId, rewardId: pending.id, idempotencyKey: newIdempotencyKey(),
      });
      setBalance(data.tokenBalance);
      setFlash(`You got the ${pending.name}!`);
      setPending(null);
      load();
      setTimeout(() => setFlash(null), 2400);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && detail.error === "insufficient_tokens") {
        setErr(detail.message || "Keep playing to earn more tokens!");
      } else {
        setErr(formatApiErrorDetail(detail || e.message));
      }
      setPending(null);
    } finally { setBusy(false); }
  }

  // Auto-clear the gentle "Not yet" flash after ~3s so kids don't see it
  // linger indefinitely.
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 3000);
    return () => clearTimeout(t);
  }, [flash]);

  async function toggleEquip(r) {
    try {
      await api.post("/rewards/equip", {
        childProfileId: profileId, rewardId: r.id, equipped: !r.equipped,
      });
      load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    }
  }

  return (
    <div className="wmleye-app rewards-room" data-testid="rewards-room">
      <header className="rewards-room__header">
        <BigButton variant="ghost" onClick={() => navigate(`/child/${profileId}/home`)} data-testid="rewards-back">
          ← Home
        </BigButton>
        <h1>My Rewards</h1>
        <div className="rewards-room__balance" data-testid="rewards-balance">
          <Coins size={22} /> <b>{balance}</b>
        </div>
      </header>

      <div className="rewards-room__tabs" role="tablist">
        {CATEGORY_TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={category === t.key}
            className={category === t.key ? "active" : ""}
            onClick={() => setCategory(t.key)}
            data-testid={`rewards-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {err && <div className="rewards-room__notice" data-testid="rewards-notice">{err}</div>}
      {flash && <div className="rewards-room__flash" data-testid="rewards-flash">{flash}</div>}

      {loading ? (
        <p style={{ textAlign: "center" }}>Loading your rewards…</p>
      ) : items.length === 0 ? (
        <div className="rewards-room__empty" data-testid="rewards-empty">
          <img src="/api/uploads/mascot_ollie.png" alt="Ollie the owl" onError={(e) => e.currentTarget.style.display = "none"} />
          <p>Play levels to earn tokens, then come back to shop!</p>
        </div>
      ) : (
        <div className="rewards-room__grid" data-testid="rewards-grid">
          {filtered.map((r) => {
            const affordable = balance >= r.tokenCost;
            const canEquip = ["avatar_accessory", "room_decoration"].includes(r.category);
            return (
              <motion.div
                key={r.id}
                whileHover={{ y: -4 }}
                className={`reward-card ${r.owned ? "reward-card--owned" : ""}`}
                data-testid={`reward-card-${r.slug}`}
              >
                <div className="reward-card__thumb">
                  {r.thumbnailUrl ? <img src={r.thumbnailUrl} alt={r.name} /> : <div className="reward-card__placeholder">🎁</div>}
                  {r.owned && (
                    <span className="reward-card__owned-check" aria-label="Owned" data-testid={`reward-owned-${r.slug}`}>
                      <CheckCircle2 size={22} color="#4c956c" />
                    </span>
                  )}
                </div>
                <h3>{r.name}</h3>
                <p className="reward-card__desc">{r.description}</p>
                <div className="reward-card__cost">
                  <Coins size={16} /> <b>{r.tokenCost}</b>
                </div>
                {r.owned ? (
                  canEquip ? (
                    <BigButton
                      variant={r.equipped ? "sun" : "ghost"}
                      onClick={() => toggleEquip(r)}
                      data-testid={`reward-equip-${r.slug}`}
                    >
                      {r.equipped ? "Equipped ✓" : "Equip"}
                    </BigButton>
                  ) : (
                    <BigButton variant="ghost" disabled data-testid={`reward-owned-btn-${r.slug}`}>
                      In collection
                    </BigButton>
                  )
                ) : affordable ? (
                  <BigButton onClick={() => setPending(r)} data-testid={`reward-buy-${r.slug}`}>
                    Buy
                  </BigButton>
                ) : (
                  <BigButton
                    variant="ghost"
                    onClick={() => setFlash(`You need ${r.tokenCost - balance} more tokens. Keep playing to earn them!`)}
                    data-testid={`reward-buy-${r.slug}`}
                    aria-label={`Not enough tokens for ${r.name}. Costs ${r.tokenCost}, you have ${balance}.`}
                  >
                    Not yet — {r.tokenCost - balance} more
                  </BigButton>
                )}
              </motion.div>
            );
          })}
        </div>
      )}

      {pending && (
        <div className="rewards-modal" role="dialog" aria-modal data-testid="rewards-confirm">
          <div className="rewards-modal__panel">
            <h2>Buy this {pending.category === "sticker" ? "Sticker" :
              pending.category === "avatar_accessory" ? "Accessory" :
              pending.category === "room_decoration" ? "Decoration" : "Bonus"}?</h2>
            <p><b>{pending.name}</b> for <b>{pending.tokenCost}</b> tokens.</p>
            <p style={{ color: "#5a4f4a" }}>You'll have {balance - pending.tokenCost} tokens left.</p>
            <div className="rewards-modal__actions">
              <BigButton variant="ghost" onClick={() => setPending(null)} data-testid="rewards-confirm-cancel">Not now</BigButton>
              <BigButton onClick={confirmPurchase} disabled={busy} data-testid="rewards-confirm-buy">
                {busy ? "…" : "Yes, buy it!"}
              </BigButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
